import { Link, useSearchParams } from 'react-router';
import type { Renewal, Service } from '@bekebi/contracts/api';
import { isValidIsoDate } from '@bekebi/domain';
import { Button, buttonClass } from '../../components/button';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { useServices } from '../services/api';
import { formatDate } from '../services/format';
import { useUpcomingRenewals } from './api';
import { RenewalStateBadge, formatMoney } from './present';

/**
 * Renewals overview (/app/renewals).
 *   - Open renewal terms, soonest first — GET /api/renewals/upcoming (cursor pagination), with the
 *     API's own "renewing on or before" filter kept in the URL (?before=YYYY-MM-DD).
 *   - Services with no open term, from the services' own renewal summary (no extra request logic).
 * Condition, countdown and price are shown exactly as the API returns them.
 */
export function RenewalsOverview() {
  const [params, setParams] = useSearchParams();
  const rawBefore = params.get('before');
  const before = rawBefore && isValidIsoDate(rawBefore) ? rawBefore : null;
  const upcoming = useUpcomingRenewals(before);
  const services = useServices();
  const canManage = useHasPermission('renewals.manage');

  const setBefore = (value: string) => {
    const next = new URLSearchParams(params);
    if (value && isValidIsoDate(value)) next.set('before', value);
    else next.delete('before');
    setParams(next, { replace: true });
  };

  const serviceById = new Map((services.data ?? []).map((s) => [s.id, s]));
  const terms = upcoming.data?.pages.flatMap((p) => p.items) ?? [];
  const withoutTerm = (services.data ?? []).filter((s) => s.renewal === null);

  const addLink = canManage ? (
    <Link to="/app/renewals/new" className={buttonClass('primary')}>
      Open a renewal term
    </Link>
  ) : null;

  return (
    <div className="max-w-5xl space-y-8">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1>Renewals</h1>
          {addLink}
        </div>
        <p className="mt-1 text-sm text-muted">
          Renewal terms of Bekebi&rsquo;s services. Conditions and countdowns are worked out by the
          portal in Lagos time.
        </p>
      </div>

      <section aria-labelledby="open-terms">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 id="open-terms">Open renewal terms</h2>
          <div>
            <label htmlFor="filter-before" className="block text-xs font-medium text-muted">
              Renewing on or before
            </label>
            <div className="mt-1 flex gap-2">
              <input
                id="filter-before"
                type="date"
                value={before ?? ''}
                onChange={(e) => setBefore(e.target.value)}
                className="min-h-10 rounded-md border border-line-strong bg-surface px-3 text-sm"
              />
              {before ? (
                <Button variant="secondary" onClick={() => setBefore('')}>
                  Clear
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4">
          {upcoming.isPending ? (
            <LoadingState label="Loading renewal terms…" />
          ) : upcoming.isError ? (
            <ErrorState
              title="Renewal terms could not be loaded"
              message={upcoming.error.message}
              onRetry={() => void upcoming.refetch()}
            />
          ) : terms.length === 0 ? (
            before ? (
              <EmptyState title={`No open terms renew on or before ${formatDate(before)}.`} />
            ) : (
              <EmptyState
                title="No renewals yet."
                description="No service has an open renewal term."
                action={addLink}
              />
            )
          ) : (
            <>
              <div
                aria-hidden="true"
                className="hidden grid-cols-[1.4fr_1fr_1.6fr_1fr] gap-4 px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted md:grid"
              >
                <span>Renews</span>
                <span>Renewal date</span>
                <span>Condition</span>
                <span>Price</span>
              </div>
              <ul aria-label="Open renewal terms" className="space-y-2">
                {terms.map((renewal) => (
                  <li key={renewal.id}>
                    <TermRow
                      renewal={renewal}
                      service={
                        renewal.serviceId !== null ? serviceById.get(renewal.serviceId) : undefined
                      }
                    />
                  </li>
                ))}
              </ul>
              {upcoming.hasNextPage ? (
                <div className="mt-4">
                  <Button
                    variant="secondary"
                    onClick={() => void upcoming.fetchNextPage()}
                    disabled={upcoming.isFetchingNextPage}
                  >
                    {upcoming.isFetchingNextPage ? 'Loading…' : 'Show more terms'}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>

      {services.isError ? (
        <section aria-labelledby="no-term">
          <h2 id="no-term">Services without an open renewal term</h2>
          <div className="mt-4">
            <ErrorState
              title="Services could not be loaded"
              message={`It is not known which services lack an open renewal term: ${services.error.message}`}
              onRetry={() => void services.refetch()}
            />
          </div>
        </section>
      ) : null}

      {services.data && withoutTerm.length > 0 ? (
        <section aria-labelledby="no-term">
          <h2 id="no-term">Services without an open renewal term</h2>
          <ul aria-label="Services without an open renewal term" className="mt-4 space-y-2">
            {withoutTerm.map((service) => (
              <li
                key={service.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-surface p-4 text-sm"
              >
                <Link
                  to={`/app/services/${service.id}`}
                  className="font-semibold text-fg underline-offset-4 hover:underline"
                >
                  {serviceLabel(service)}
                </Link>
                {canManage ? (
                  <Link
                    to={`/app/renewals/new?service=${service.id}`}
                    aria-label={`Open a renewal term for ${serviceLabel(service)}`}
                    className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
                  >
                    Open a renewal term
                  </Link>
                ) : (
                  <span className="text-muted">No open renewal term</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function serviceLabel(service: Service): string {
  return service.type === 'website' && service.accessUrl
    ? `${service.name} (${service.accessUrl})`
    : service.name;
}

function TermRow({ renewal, service }: { renewal: Renewal; service: Service | undefined }) {
  return (
    <article className="grid gap-2 rounded-lg border border-line bg-surface p-4 text-sm md:grid-cols-[1.4fr_1fr_1.6fr_1fr] md:items-center md:gap-4">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold">
          <Link
            to={`/app/renewals/${renewal.id}`}
            className="text-fg underline-offset-4 hover:underline"
          >
            {service ? serviceLabel(service) : renewal.subjectName} renewal
          </Link>
        </h3>
        <p className="text-xs text-muted">Term started {formatDate(renewal.termStart)}</p>
      </div>
      <div className="text-fg-2">
        <span className="text-muted md:hidden">Renewal date: </span>
        {formatDate(renewal.renewalDate)}
      </div>
      <div>
        <RenewalStateBadge renewal={renewal} />
      </div>
      <div className="text-fg-2">
        <span className="text-muted md:hidden">Price: </span>
        {formatMoney(renewal.price.amount, renewal.price.currency)}
      </div>
    </article>
  );
}
